<template>
    <v-form @submit.prevent="addTeamPlan" ref="form">
        <v-card class="rounded-4">
            <v-toolbar flat height="55px">
                <span class="font-weight-bold">Create Members Plan</span>
                <v-spacer></v-spacer>
                <v-btn icon @click.native="closeDialog()" >
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="form-container mt-10">

                <!-- Team Members list -->
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-row">
                        <v-autocomplete
                            label="Select Team Members"
                            v-model="teamMemberIds"
                            :disabled="disabled"
                            :items="team_members"
                            :loading="loading_teammembers"
                            :rules="[rules.required]"
                            chips
                            color="primary lighten-2"
                            item-text="name"
                            item-value="userId"
                            multiple
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="data.item.avatar"></v-img>
                                    </v-avatar>
                                    {{ data.item.name }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                <v-list-tile-avatar>
                                    <img :src="data.item.avatar">
                                </v-list-tile-avatar>
                                <v-list-tile-content>
                                    <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                    <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>

                <!-- Workshifts -->
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-row"> 
                        <v-autocomplete
                            label="Workshift"
                            v-model="workShiftId"
                            :disabled="disabled"
                            :items="workshifts_data"
                            :loading="loading"
                            :rules="[rules.required]"
                            chips
                            color="primary"
                            item-text="name"
                            item-value="workShiftId"
                            @change="changeColor" 
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi ml-0" :color="color">
                                     <span :style="{'color': getContrastColor(color)}">
                                        {{ data.item.name }}
                                    </span>
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout> 

                <!-- Start and End dates -->
                <v-layout row wrap class="form-row mb-0">
                    <v-flex xs12 sm6 :class="isMobile?'':'pr-15'"> 
                        <v-menu v-model="menuStartDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field label="Start Date" v-model="startDate" readonly v-on="on" :rules="[rules.required]" :disabled="disabled"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="startDate" @input="updateData" :allowed-dates="allowedStartDates"></v-date-picker>
                        </v-menu>
                    </v-flex>   
                    <v-flex xs12 sm6 :class="isMobile?'':'pl-15'"> 
                        <v-menu v-model="menuEndDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field label="End Date" v-model="endDate" readonly v-on="on" :rules="[rules.required]" :disabled="disabled"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="endDate" @input="menuEndDate=false" :allowed-dates="allowedEndDates"></v-date-picker>
                        </v-menu>
                    </v-flex>   
                </v-layout>

                <!-- Overwrite existing -->
                <v-layout row wrap class="form-row mt-n10 mb-15">
                    <v-flex xs12 sm12>
                        <v-switch
                            v-model="overwriteExisting"
                            label="Overwrite existing"
                            color="primary"
                            persistent-hint
                            :hint="overwriteExisting?'Will overwrite if workshift already exist':'Will not overwrite if workshift already exist'"
                        ></v-switch> 
                        <v-switch
                            v-model="includeWeekends"
                            label="Include weekends"
                            color="primary"
                            persistent-hint
                            :hint="includeWeekends?'Will include weekends if within selected date range':'Will not include weekends if within selected date range'"
                        ></v-switch> 
                    </v-flex>
                </v-layout>

            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="clearForm" :disabled="disabled">Clear</v-btn>
                <v-btn color="primary" type="submit" :disabled="disabled" :loading="loading" dark class="mr-25">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import teamsApi from '@/api/team'
import workshiftsplanApi from '@/api/workshiftsplan'
import awsS3Helper from '@/helpers/aws-s3'
import { getContrastColor } from '@/helpers/font-color'

export default {
    data () {
        return {
            loading: false,
            disabled: false,
            error: false,
            errorMessage: null,
            loading_teammembers: false,
            rules: {
                required: value => !!value || 'Required',
            },       
            team_members: [],
            workshifts_data: [],
            teamMemberIds: [],
            includeWeekends: false,
            overwriteExisting: false,
            menuStartDate: false,
            startDate: null,
            menuEndDate: false,
            endDate: null,
            workShiftId: null,
            selectedTeamId: null,
            fiscalYear: null,
            pageKey: null,
            color: null,
        }
    },
    methods: {
        getContrastColor,
        changeColor(val) {
            const data = this.workshifts_data.find(x => x.workShiftId == val)
            this.color = data ? data.color :  'transparent'
        },
        async getMembersBySelectedTeam() {
            try {
                this.loading_teammembers = true
                const response = await teamsApi.getMembersBySelectedTeam(this.selectedTeamId)
                if (response && response.status == 200) {
                    if (response.data.teamMembers && response.data.teamMembers.length) {
                        this.team_members = response.data.teamMembers.map(x => ({
                            userId: x.userId,
                            name: `${x.firstName} ${x.lastName}`,
                            avatar: awsS3Helper.getAvatarUrl(x.userId)
                        }));
                    }
                }
                this.loading_teammembers = false
            }catch(error) {
                this.errorMessage = error
                this.error = true
            }
        },
        async getWorkShifts() {
            try {
                this.loading = true
                const response = await workshiftsplanApi.getWorkShifts(this.pageKey) 
                if (response && response.status == 200) {
                    this.workshifts_data = response.data.items
                }
                this.loading = false
            }catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        async addTeamPlan() {
            try {
                this.loading = true
                if (this.$refs.form.validate()) {
                    this.loading = true
                    this.disabled = true
                    const data = {
                        teamId: this.selectedTeamId,
                        teamMemberIds: this.teamMemberIds,
                        workShiftId: this.workShiftId,
                        startDate: this.startDate,
                        endDate: this.endDate,
                        userId: 0,
                        fiscalYear: this.fiscalYear,
                        includeWeekends: this.includeWeekends,
                        overwriteExisting: this.overwriteExisting
                    }
                    const response = await workshiftsplanApi.updateByTeamMembers(data, this.pageKey)
                    if (response && response.status == 200) {
                        this.$emit('send',null)                        
                        this.closeDialog()
                    }
                    this.disabled = false
                    this.loading = false
                }
                this.loading = false
            }catch(error){
                console.log(error)
                this.error = true
                this.errorMessage = error
            }
        },
        updateData() {
            this.menuStartDate=false
            if (this.$moment(this.startDate).format('YYYY-MM-DD') > this.$moment(this.endDate).format('YYYY-MM-DD')) {
                this.endDate = this.startDate
            }
        },
        allowedStartDates(val) {
            //allow only current or future dates in start date
            const currentDate = new Date()
            return this.$moment(val).format('YYYY-MM-DD') >= this.$moment(currentDate).format('YYYY-MM-DD')
        },
        allowedEndDates(val) {
            //allow only dates equal or greater than start date
            return this.$moment(val).format('YYYY-MM-DD') >= this.$moment(this.startDate).format('YYYY-MM-DD')
        },
        async setData(data) {
            this.selectedTeamId = data.teamId
            this.fiscalYear = data.fiscalYear
            this.pageKey = data.pageKey

            if (!this.workshifts_data.length) {
                await this.getWorkShifts()                
            }

            //always pull latest since team can already be changed from anyware in team pages
            await this.getMembersBySelectedTeam() 
        },
        closeDialog() {
            this.clearForm()
            this.$emit('update:dialogAddTeamMembersPlan', false)
        },
        clearForm() {
            this.$refs.form.reset()
            this.loading = false
            this.error = false
            this.errorMessage = ''
            this.color = 'transparent'
        },
    },
}
</script>
<style scoped>
.form-container {
    padding: 0px 20px 0px 20px;
}
.form-row {
    padding: 0px 25px 0px 25px;
}
</style>